import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import type {
  IActiveTabIndicatorRect,
  IIsSameTabIndicatorRectProps,
  IUseActiveTabIndicatorProps,
  IUseActiveTabIndicatorResult,
} from "./types";

const isSameRect = ({ current, next }: IIsSameTabIndicatorRectProps): boolean =>
  current !== null &&
  current.left === next.left &&
  current.top === next.top &&
  current.width === next.width &&
  current.height === next.height;

// NOTE: NSAI-7001 — where the sliding tab pill should sit. The list is read
// from the DOM rather than from props because Radix keeps the active value in
// a private context: the active trigger is found by its marker, measured
// against the list (which is `relative`, so `offsetLeft` / `offsetTop` are
// list-relative and a scrolling list carries the pill with it), and watched for
// the marker moving, a tab being added or removed, a label's text changing, and
// the row or any single tab resizing — a language switch can widen one tab
// without the list changing size (a row already at `max-w-full`), and that
// still moves the target.
export const useActiveTabIndicator = ({
  listRef,
  activeTabSelector,
}: IUseActiveTabIndicatorProps): IUseActiveTabIndicatorResult => {
  const [rect, setRect] = useState<IActiveTabIndicatorRect | null>(null);
  // NOTE: The first placement is made without a transition, so the pill does
  // not fly in from the list's corner on mount; every later move slides.
  const [isAnimated, setIsAnimated] = useState(false);
  const hasPlacedRef = useRef(false);

  const measure = useCallback((): void => {
    const activeTab =
      listRef.current?.querySelector<HTMLElement>(activeTabSelector);

    if (!activeTab) {
      hasPlacedRef.current = false;
      setRect(null);
      return;
    }

    const next = {
      left: activeTab.offsetLeft,
      top: activeTab.offsetTop,
      width: activeTab.offsetWidth,
      height: activeTab.offsetHeight,
    };

    setIsAnimated(hasPlacedRef.current);
    hasPlacedRef.current = true;
    setRect((current) => (isSameRect({ current, next }) ? current : next));
  }, [activeTabSelector, listRef]);

  useLayoutEffect(() => {
    measure();
  }, [measure]);

  useEffect(() => {
    const list = listRef.current;

    if (!list) {
      return undefined;
    }

    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(() => measure());

    // NOTE: The list and every tab in it, re-collected whenever the tabs
    // change, so a tab that grows inside an unchanged row is still seen.
    const observeSizes = (): void => {
      if (!resizeObserver) {
        return;
      }
      resizeObserver.disconnect();
      resizeObserver.observe(list);
      Array.from(list.children).forEach((child) => {
        resizeObserver.observe(child);
      });
    };

    observeSizes();

    const mutationObserver = new MutationObserver(() => {
      observeSizes();
      measure();
    });
    mutationObserver.observe(list, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ["aria-selected", "data-active", "data-state"],
    });

    return (): void => {
      mutationObserver.disconnect();
      resizeObserver?.disconnect();
    };
  }, [listRef, measure]);

  return { rect, isAnimated };
};
